import { ReactElement, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useMutation, useApolloClient, gql } from '@apollo/client'
import styled from 'styled-components'

import { WarningIcon } from '@10xLabs/react-web/components/Icons/Warning'
import Dropdown from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { DotsIcon } from 'components/Icons/DotsIcon'
import { resolvers as itemsResolvers } from 'resolvers/items'
import { GTItem } from 'gql/types.generated'
import IconButton from 'components/v2/IconButton'

const DeactivateWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledWarningIcon = styled(WarningIcon)`
  margin-left: 10px;
`

const ACTIVATE_ITEM = gql`
  mutation ActivateItem($itemId: ID!) {
    activateItem(input: { ID: $itemId }) {
      id
    }
  }
`

const ACTIVATE_LOCAL_ITEM = gql`
  mutation ActivateLocalItem($itemId: ID!) {
    activateItem(id: $itemId) @client
  }
`

const DEACTIVATE_ITEM = gql`
  mutation DeactivateItem($itemId: ID!) {
    deactivateItem(input: { ID: $itemId }) {
      id
    }
  }
`

const DEACTIVATE_LOCAL_ITEM = gql`
  mutation DeactivateLocalItem($itemId: ID!) {
    deactivateItem(id: $itemId) @client
  }
`

export const ProductsActions = ({ item }: { item: GTItem }): ReactElement => {
  const client = useApolloClient()
  client.addResolvers(itemsResolvers)

  const [isOpenDropdown, setOpenDropdown] = useState(false)

  const [activateLocalItem] = useMutation(ACTIVATE_LOCAL_ITEM)
  const [activateItem, { loading: activateLoading, error: activateError }] = useMutation(ACTIVATE_ITEM, {
    onCompleted() {
      activateLocalItem({
        variables: {
          itemId: item.id
        }
      })
    }
  })
  const [deactivateLocalItem] = useMutation(DEACTIVATE_LOCAL_ITEM)
  const [deactivateItem, { loading: deactivateLoading, error: deactivateError }] = useMutation(DEACTIVATE_ITEM, {
    onCompleted() {
      deactivateLocalItem({
        variables: {
          itemId: item.id
        }
      })
    }
  })

  const toggleItemActivation = (): void => {
    if (!item.isActive) {
      activateItem({
        variables: {
          itemId: item.id
        }
      })
    } else {
      deactivateItem({
        variables: {
          itemId: item.id
        }
      })
    }
  }

  const toggleDropdown = (): void => {
    setOpenDropdown(!isOpenDropdown)
  }

  return (
    <Dropdown.Wrapper noPadding noIndicator onClickAway={(): void => setOpenDropdown(false)}>
      <IconButton icon={<DotsIcon />} onClick={toggleDropdown} noBorder smallPadding />
      <Dropdown.Content open={isOpenDropdown} noIndicator left>
        <List.Container>
          <List.Item noPadding>
            <List.Link to={`/snacks/products/${item.id}`}>
              <FormattedMessage id="spanish.products.list.actions.edit" defaultMessage="Editar producto" />
            </List.Link>
          </List.Item>
          <List.Item action onClick={toggleItemActivation} loading={activateLoading || deactivateLoading}>
            <DeactivateWrapper>
              {!item.isActive ? (
                <FormattedMessage id="spanish.dictionary.activate" defaultMessage="Activar" />
              ) : (
                <FormattedMessage id="spanish.dictionary.deactivate" defaultMessage="Desactivar" />
              )}
              {(deactivateError || activateError) && <StyledWarningIcon />}
            </DeactivateWrapper>
          </List.Item>
        </List.Container>
      </Dropdown.Content>
    </Dropdown.Wrapper>
  )
}
